import { createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";
import { buildQueryParams } from "../../utils/commonMethods";

const incidentTaskCreationTriggerAdapter = createEntityAdapter({
  selectId: (trigger) => trigger.id,
});

const triggerAdapter = createEntityAdapter({
  selectId: (trigger) => trigger.id,
});

const incidentTypeAdapter = createEntityAdapter({
  selectId: (type) => type.id,
});

const incidentSubTypeAdapter = createEntityAdapter({
  selectId: (type) => type.id,
});

const incidentTaskCreationTriggerInitialState =
  incidentTaskCreationTriggerAdapter.getInitialState();

const triggerInitialState = triggerAdapter.getInitialState();

const incidentTypeInitialState = incidentTypeAdapter.getInitialState();
const incidentSubTypeInitialState = incidentSubTypeAdapter.getInitialState();

export const taskAutomationSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getIncidentTaskCreationTriggers: builder.query({
      query: () => `/incident-task-creation-triggers`,
      transformResponse: (responseData) => {
        const loadedIncidentTaskCreationTriggers = responseData.map((item) => {
          const {
            id,
            type,
            subType,
            task,
            inDays,
            allowedDocumentsToLink,
            trigger,
            group,
            postCompletionTask,
            assignedTo,
            taskDescription,
            conditions,
          } = item;

          return {
            id,
            name: task?.name ?? "-",
            taskDescription: taskDescription ?? "-",
            incidentGroup: group?.description ?? "-",
            incident: type?.description ?? "-",
            incidentSubType: subType?.description ?? "-",
            trigger: trigger?.type?.description,
            dueInDays: inDays ?? "-",
            allowedDocumentsToLink,
            taskId: task?.id,
            triggerId: trigger?.id,
            postCompletionTaskId: postCompletionTask?.id,
            incidentGroupId: group?.id,
            incidentTypeId: type?.id,
            incidentSubTypeId: subType?.id,
            assignedTo,
            conditions,
          };
        });
        return incidentTaskCreationTriggerAdapter.setAll(
          incidentTaskCreationTriggerInitialState,
          loadedIncidentTaskCreationTriggers
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentTaskCreationTriggers", id: "ERROR" }];
        }
        return [
          { type: "IncidentTaskCreationTriggers", id: "LIST" },
          ...(result?.ids || []).map((id) => ({
            type: "IncidentTaskCreationTriggers",
            id,
          })),
        ];
      },
    }),
    getTrigger: builder.query({
      query: () => `/triggers`,
      transformResponse: (responseData) => {
        const loadedTriggers = responseData;
        return triggerAdapter.setAll(triggerInitialState, loadedTriggers);
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "Triggers", id: "ERROR" }];
        }
        return [
          { type: "Triggers", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "Triggers", id })),
        ];
      },
    }),
    getIncidentTypes: builder.query({
      query: () => `/incident-types`,
      transformResponse: (responseData) => {
        const loadedIncidentTypes = responseData;
        return incidentTypeAdapter.setAll(
          incidentTypeInitialState,
          loadedIncidentTypes
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentTypes", id: "ERROR" }];
        }
        return [
          { type: "IncidentTypes", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "IncidentTypes", id })),
        ];
      },
    }),
    getIncidentSubTypes: builder.query({
      query: (id) => `/incident-types/${id}/sub-types`,
      transformResponse: (responseData) => {
        const loadedIncidentSubTypes = responseData;
        return incidentSubTypeAdapter.setAll(
          incidentSubTypeInitialState,
          loadedIncidentSubTypes
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentSubTypes", id: "ERROR" }];
        }
        return [
          { type: "IncidentSubTypes", id: "LIST" },
          ...(result?.ids || []).map((id) => ({
            type: "IncidentSubTypes",
            id,
          })),
        ];
      },
    }),
    getCostCenters: builder.query({
      query: ({ costCenterKey, query, page, pageSize }) => {
        const params = {
          costCenterKey,
          query,
          page,
          pageSize,
        };

        const queryString = buildQueryParams(params);
        return `/hierarchy/cost-centers?${queryString}`;
      },
    }),
    getCostCenterDetails: builder.query({
      query: (key) => `/hierarchy/cost-centers/${key}`,
    }),
    getCreationInfo: builder.query({
      query: () => `/incident-task-creation-triggers/creation-info`,
    }),
    createIncidentTaskTrigger: builder.mutation({
      query: (data) => ({
        url: `/incident-task-creation-triggers`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      invalidatesTags: [
        { type: "IncidentTaskCreationTriggers", id: "LIST" },
        { type: "Tasks", id: "LIST" },
      ],
    }),
    updateIncidentTaskTrigger: builder.mutation({
      query: (data) => ({
        url: `/incident-task-creation-triggers/${data.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
      invalidatesTags: [
        { type: "IncidentTaskCreationTriggers", id: "LIST" },
        { type: "Tasks", id: "LIST" },
      ],
    }),
    deleteIncidentTaskTrigger: builder.mutation({
      query: (id) => ({
        url: `/incident-task-creation-triggers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "IncidentTaskCreationTriggers", id: "LIST" },
        { type: "Tasks", id: "LIST" },
      ],
    }),
  }),
});
export const {
  useGetIncidentTaskCreationTriggersQuery,
  useGetTriggerQuery,
  useGetIncidentSubTypesQuery,
  useGetIncidentTypesQuery,
  useGetCostCentersQuery,
  useGetCreationInfoQuery,
  useCreateIncidentTaskTriggerMutation,
  useUpdateIncidentTaskTriggerMutation,
  useDeleteIncidentTaskTriggerMutation,
  useGetCostCenterDetailsQuery,
  useLazyGetCostCenterDetailsQuery,
} = taskAutomationSlice;
